// Browser-safe compact/expand for content-addressed image storage.
//
// Invoice/company/quotation data repeats the same base64 logo, seal,
// and background image across every row. This module factors those
// repeated blobs out into a single `_assets` map keyed by SHA-256 hash
// and replaces each occurrence with { $ref: "<hash>" }. A thin expand
// step reverses the transform when readers need the inlined data.
//
// Used by:
//   - services/assetStorage.js  — IDB wrapper (compact-on-write, expand-on-read)
//   - services/cloudSyncService.js — `_assets` carried through upload/apply
// A near-identical implementation lives in scripts/compact-backup.js for
// node-side one-off transforms. Keep them in sync when adding new fields.

export const ASSETS_KEY = '_assets';

const IMAGE_FIELDS = ['image', 'sealImage', 'backgroundImage'];
const IMAGE_PAYLOAD_FIELDS = ['base64', 'uri', 'dataUrl', 'data'];
const MIN_INLINE_BYTES = 512;

async function sha256Hex(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  const bytes = new Uint8Array(buf);
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, '0');
  return out;
}

function isBigString(v) {
  return typeof v === 'string' && v.length >= MIN_INLINE_BYTES;
}

function isLikelyImageString(v) {
  if (!isBigString(v)) return false;
  if (v.startsWith('data:')) return true;
  return /^[A-Za-z0-9+/=\s]+$/.test(v.slice(0, 64));
}

function isRef(node) {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return false;
  const keys = Object.keys(node);
  return keys.length === 1 && keys[0] === '$ref' && typeof node.$ref === 'string';
}

async function dedupString(v, existingAssets, newAssets) {
  const hash = await sha256Hex(v);
  if (!existingAssets[hash] && !newAssets.has(hash)) newAssets.set(hash, v);
  return { $ref: hash };
}

async function compactNode(node, existing, newAssets) {
  if (!node || typeof node !== 'object') return node;
  if (Array.isArray(node)) {
    const out = new Array(node.length);
    for (let i = 0; i < node.length; i++) out[i] = await compactNode(node[i], existing, newAssets);
    return out;
  }
  if (isRef(node)) return node;

  const out = {};
  for (const k of Object.keys(node)) {
    const v = node[k];

    if (IMAGE_FIELDS.includes(k) && isLikelyImageString(v)) {
      out[k] = await dedupString(v, existing, newAssets);
      continue;
    }

    if (IMAGE_FIELDS.includes(k) && v && typeof v === 'object' && !Array.isArray(v) && !isRef(v)) {
      const inner = {};
      for (const ik of Object.keys(v)) {
        const iv = v[ik];
        if (IMAGE_PAYLOAD_FIELDS.includes(ik) && isLikelyImageString(iv)) {
          inner[ik] = await dedupString(iv, existing, newAssets);
        } else {
          inner[ik] = iv;
        }
      }
      out[k] = inner;
      continue;
    }

    if (IMAGE_PAYLOAD_FIELDS.includes(k) && isLikelyImageString(v)) {
      out[k] = await dedupString(v, existing, newAssets);
      continue;
    }

    out[k] = await compactNode(v, existing, newAssets);
  }
  return out;
}

// Returns { compact, newAssets: Map<hash, string> }.
// `existingAssets` is the assets map already known to the caller; entries
// whose hash is already present there are not duplicated into newAssets.
export async function compactValue(value, existingAssets = {}) {
  const newAssets = new Map();
  const compact = await compactNode(value, existingAssets, newAssets);
  return { compact, newAssets };
}

// Expand — pure, synchronous. Any `{ $ref: "<hash>" }` is replaced with
// assets[hash] (or left as-is if the hash is unknown).
export function expandValue(value, assets) {
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    const out = new Array(value.length);
    for (let i = 0; i < value.length; i++) out[i] = expandValue(value[i], assets);
    return out;
  }
  if (isRef(value)) {
    const hit = assets && assets[value.$ref];
    return hit !== undefined ? hit : value;
  }
  const out = {};
  for (const k of Object.keys(value)) out[k] = expandValue(value[k], assets);
  return out;
}